import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Camera, AlertCircle, Check } from 'lucide-react';

interface ImageUploadProps {
  onImageSelect: (file: File, preview: string) => void;
  onImageRemove: () => void;
  currentImage?: string;
  isUploading?: boolean;
  uploadProgress?: number;
  maxSizeBytes?: number;
  acceptedFormats?: string[];
  className?: string;
}

/**
 * Advanced Image Upload Component
 * Features: drag & drop, preview, validation, progress indication
 */
export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageSelect,
  onImageRemove,
  currentImage,
  isUploading = false,
  uploadProgress = 0,
  maxSizeBytes = 5 * 1024 * 1024, // 5MB default
  acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Validates uploaded file against size and format requirements
   */
  const validateFile = (file: File): string | null => {
    // Check file type
    if (!acceptedFormats.includes(file.type)) {
      return `Formato no válido. Acepta: ${acceptedFormats.map(f => f.split('/')[1]).join(', ')}`;
    }

    // Check file size
    if (file.size > maxSizeBytes) {
      const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(1);
      return `El archivo es demasiado grande. Máximo: ${maxSizeMB}MB`;
    }

    return null;
  };

  /**
   * Processes selected file and creates preview
   */
  const handleFileSelect = useCallback(async (file: File) => {
    setError('');
    
    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    // Call parent callback immediately with file and preview
    onImageSelect(file, previewUrl);
  }, [onImageSelect, maxSizeBytes, acceptedFormats]);

  /**
   * Handles file input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handles drag and drop events
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Opens file picker
   */
  const openFilePicker = (e?: React.MouseEvent) => {
    // Prevenir comportamiento por defecto si es llamado desde un botón
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    fileInputRef.current?.click();
  };

  /**
   * Removes current image
   */
  const handleRemoveImage = () => {
    setError('');
    onImageRemove();
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200
          ${isDragging 
            ? 'border-blue-400 bg-blue-50' 
            : currentImage 
              ? 'border-green-300 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${error ? 'border-red-300 bg-red-50' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(',')}
          onChange={handleInputChange}
          className="hidden"
          aria-label="Seleccionar imagen de perfil"
        />

        {/* Current Image Preview */}
        {currentImage && !isUploading && (
          <div className="relative inline-block">
            <img
              src={currentImage}
              alt="Vista previa del perfil"
              className="w-32 h-32 rounded-full object-cover object-center border-4 border-white shadow-lg"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center"
              aria-label="Eliminar imagen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-4">
            <div className="w-24 h-24 mx-auto bg-gray-200 rounded-full animate-pulse flex items-center justify-center">
              <Upload className="w-8 h-8 text-gray-400" />
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Subiendo a Supabase Storage...</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">{Math.round(uploadProgress)}%</p>
            </div>
          </div>
        )}

        {/* Upload Area (when no image) */}
        {!currentImage && !isUploading && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
              <Camera className="w-8 h-8 text-gray-400" />
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-700">
                {isDragging ? 'Suelta la imagen aquí' : 'Sube tu foto de perfil'}
              </p>
              <p className="text-sm text-gray-500">
                Arrastra una imagen o{' '}
                <button
                  onClick={openFilePicker}
                  className="text-blue-600 hover:text-blue-700 font-medium underline"
                  type="button"
                >
                  selecciona un archivo
                </button>
              </p>
              <p className="text-xs text-gray-400">
                JPG, PNG, GIF, WEBP hasta {(maxSizeBytes / (1024 * 1024)).toFixed(1)}MB
              </p>
              <p className="text-xs text-blue-600">
                Se guardará en Supabase Storage
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Upload Button (alternative) */}
      {!currentImage && !isUploading && (
        <button
          type="button"
          onClick={openFilePicker}
          disabled={isUploading}
          className="w-full py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
        >
          <Upload className="w-5 h-5" />
          {isUploading ? 'Subiendo...' : 'Seleccionar imagen'}
        </button>
      )}
    </div>
  );
};